import React, { useEffect, useRef, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Fade from '@material-ui/core/Fade';
import CloseIcon from '@material-ui/icons/Close';
import { system900 } from 'Mui/styles/color';
import { dp03 } from 'Mui/styles/shadow';
import { hiddenScrollbarStyle } from 'Mui/styles/scrollerStye';
import { stylePropType } from 'utils/propTypes';

const DialogContainer = forwardRef((props, ref) => {
    const {
        children,
        open,
        title,
        variant = 'h6',
        isFixedWindow = false,
        disableBackdrop = false,
        disableBackdropClick = false,
        backdropColor = 'rgba(255, 255, 255, 0.60)',
        themeTextColor = system900.color,
        themeBgColor = '#FFF',
        haveCloseIcon = true,
        isInnerDialog = false,
        onClose,
        arrow = false,
        arrowStyle,
        style
    } = props;
    const zIndexRef = useRef({ zIndex: 2 });

    const onBackdropClick = () => {
        !disableBackdropClick && typeof onClose === 'function' && onClose();
    };

    useEffect(() => {
        if (isInnerDialog && zIndexRef.current) {
            zIndexRef.current.zIndex++;
        }
    }, [isInnerDialog]);

    const containerDom = (
        <>
            <Fade in={open}>
                <div
                    css={css(
                        containerStyle(themeBgColor),
                        positionStyle(isFixedWindow, zIndexRef),
                        style
                    )}
                    ref={ref}
                >
                    {arrow && <div css={[arrowBasicStyle, arrowStyle]}></div>}
                    {typeof onClose === 'function' && haveCloseIcon && (
                        <div css={css(closeContainerStyle)}>
                            <IconButton aria-label="close" onClick={onClose} size="small">
                                <CloseIcon css={css(closeIconStyle)} />
                            </IconButton>
                        </div>
                    )}
                    <Typography css={css(titleStyle(themeTextColor))} variant={variant}>
                        {title ?? <span>&nbsp;</span>}
                    </Typography>
                    {children}
                </div>
            </Fade>
            {!disableBackdrop && (
                <Fade in={open}>
                    <div
                        css={backdropStyle(isFixedWindow, backdropColor, zIndexRef)}
                        onClick={onBackdropClick}
                    ></div>
                </Fade>
            )}
        </>
    );
    if (!open) return null;
    return isFixedWindow
        ? createPortal(containerDom, document.getElementById('dialog'))
        : containerDom;
});

export default DialogContainer;

const containerStyle = themeBgColor => css`
    width: 320px;
    min-height: 96px;
    max-height: 600px;
    background: ${themeBgColor};
    border-radius: 12px;
    box-shadow: ${dp03.boxShadow};
    ${hiddenScrollbarStyle}
`;

const positionStyle = (isFixedWindow, zIndexRef) => css`
    position: ${isFixedWindow ? 'fixed' : 'absolute'};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: ${zIndexRef.current.zIndex + 1};
`;

const backdropStyle = (isFixedWindow, backdropColor, zIndexRef) => css`
    position: ${isFixedWindow ? 'fixed' : 'absolute'};
    inset: 0;
    z-index: ${zIndexRef.current.zIndex};
    background: ${backdropColor};
    backdrop-filter: blur(2px);
`;

const titleStyle = themeTextColor => css`
    text-align: center;
    color: ${themeTextColor};
    padding: 16px;
    line-height: normal;
`;

const closeContainerStyle = css`
    position: absolute;
    right: 14px;
    top: 14px;
`;

const closeIconStyle = css`
    color: ${system900.color};
`;

const arrowBasicStyle = css`
    display: block;
    position: absolute;
    right: 28px;
    top: 4px;
    transform: translateY(-100%);
    height: 0;
    border-color: transparent transparent #ffffff transparent;
    border-style: solid;
    border-width: 0 12px 16px 12px;
`;

DialogContainer.propTypes = {
    children: PropTypes.node.isRequired,
    title: PropTypes.string,
    open: PropTypes.bool,
    isFixedWindow: PropTypes.bool,
    disableBackdrop: PropTypes.bool,
    disableBackdropClick: PropTypes.bool,
    backdropColor: PropTypes.string,
    themeTextColor: PropTypes.string,
    themeBgColor: PropTypes.string,
    haveCloseIcon: PropTypes.bool,
    onClose: PropTypes.func,
    arrow: PropTypes.bool,
    arrowStyle: stylePropType,
    style: stylePropType
};
